import type { Directive, DirectiveBinding } from "vue";

const cleanupByElement = new WeakMap<HTMLElement, () => void>();

function getStackAbove(binding: DirectiveBinding<number | undefined>): number {
  return typeof binding.value === "number" ? binding.value : 0;
}

function getVisibleBottom(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}

function attach(el: HTMLElement, binding: DirectiveBinding<number | undefined>) {
  let frame = 0;

  const update = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      el.style.setProperty("--stick-bottom-offset", "0px");

      const targetBottom = getVisibleBottom() - getStackAbove(binding);
      const currentBottom = el.getBoundingClientRect().bottom;
      const offset = Math.round(targetBottom - currentBottom);

      el.style.setProperty("--stick-bottom-offset", `${offset}px`);
    });
  };

  update();
  window.visualViewport?.addEventListener("resize", update);
  window.visualViewport?.addEventListener("scroll", update);
  window.addEventListener("resize", update);
  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("orientationchange", update);

  return () => {
    cancelAnimationFrame(frame);
    window.visualViewport?.removeEventListener("resize", update);
    window.visualViewport?.removeEventListener("scroll", update);
    window.removeEventListener("resize", update);
    window.removeEventListener("scroll", update);
    window.removeEventListener("orientationchange", update);
    el.style.removeProperty("--stick-bottom-offset");
  };
}

export const stickToVisualBottom: Directive<HTMLElement, number | undefined> = {
  mounted(el, binding) {
    cleanupByElement.set(el, attach(el, binding));
  },

  updated(el, binding) {
    cleanupByElement.get(el)?.();
    cleanupByElement.set(el, attach(el, binding));
  },

  unmounted(el) {
    cleanupByElement.get(el)?.();
    cleanupByElement.delete(el);
  },
};
